import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import { Play, Pause, SkipBack, SkipForward, Music, Home } from 'lucide-react';

// Anchor: bottom-right offset. Mobile sits above the bottom nav; iPad
// drops it closer to the bottom-right corner; desktop is pinned flush
// to the bottom-right corner of the viewport. User asked for desktop
// "a lot lower" and iPad "a little lower" relative to iter141.
const ANCHOR_RIGHT = '12px';
const MOBILE_BOTTOM = 'calc(72px + env(safe-area-inset-bottom, 0px) + 35px)';
const TABLET_BOTTOM = 'calc(env(safe-area-inset-bottom, 0px) + 24px)';
const DESKTOP_BOTTOM = 'calc(env(safe-area-inset-bottom, 0px) + 10px)';

const useViewportBottom = () => {
  const [bottom, setBottom] = useState(MOBILE_BOTTOM);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth || 0;
      if (w >= 1024) setBottom(DESKTOP_BOTTOM);
      else if (w >= 768) setBottom(TABLET_BOTTOM);
      else setBottom(MOBILE_BOTTOM);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);
  return bottom;
};

const MiniPlayer = () => {
  const navigate = useNavigate();
  const ANCHOR_BOTTOM = useViewportBottom();
  const {
    currentSong,
    isPlaying,
    togglePlayPause,
    playNext,
    playPrevious,
    getCharacterColor,
    currentCharacter,
  } = useMusicPlayer();

  const [expanded, setExpanded] = useState(false);

  // Fade icon out if paused for 30s+
  const [fadedOut, setFadedOut] = useState(false);
  useEffect(() => {
    if (isPlaying || expanded) {
      setFadedOut(false);
      return;
    }
    const t = setTimeout(() => setFadedOut(true), 30000);
    return () => clearTimeout(t);
  }, [isPlaying, expanded]);

  if (!currentSong) return null;

  const color = getCharacterColor();

  const goHome = () => {
    if (currentCharacter) {
      navigate(`/character/${currentCharacter}?section=music`);
    }
    setExpanded(false);
  };

  // ===== Collapsed: small floating circle =====
  if (!expanded) {
    return (
      <button
        onClick={() => { setFadedOut(false); setExpanded(true); }}
        className="fixed z-40 h-8 w-8 rounded-full bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg active:scale-95 transition-opacity duration-700"
        style={{
          right: ANCHOR_RIGHT,
          bottom: ANCHOR_BOTTOM,
          opacity: fadedOut ? 0 : 1,
          pointerEvents: fadedOut ? 'none' : 'auto',
        }}
        data-testid="mini-player-fab"
        aria-label="Open music controls"
      >
        <Music
          className={`w-3.5 h-3.5 text-white ${isPlaying ? 'mini-player-spin' : ''}`}
        />
      </button>
    );
  }

  // ===== Expanded: vertical rail with click-outside backdrop =====
  return (
    <>
      {/* Invisible backdrop — tap anywhere off the rail to collapse */}
      <div
        className="fixed inset-0 z-30"
        onClick={() => setExpanded(false)}
        data-testid="mini-player-backdrop"
      />
      <div
        className="fixed z-40 flex flex-col-reverse items-center gap-1.5 py-1.5 px-1 rounded-full bg-black/40 backdrop-blur-md border border-white/15 shadow-xl"
        style={{ right: ANCHOR_RIGHT, bottom: ANCHOR_BOTTOM }}
        data-testid="mini-player-rail"
        onClick={(e) => e.stopPropagation()}
      >
      {/* Anchor button (stays at the bottom — same place as the collapsed circle) */}
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition-colors"
        data-testid="mini-player-collapse"
        aria-label="Collapse music controls"
      >
        <Music
          className={`w-3.5 h-3.5 text-white ${isPlaying ? 'mini-player-spin' : ''}`}
        />
      </button>

      {/* Items above the anchor (rendered top-to-bottom but laid out via flex-col-reverse) */}
      <button
        onClick={playPrevious}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/85 hover:bg-white shadow-sm"
        data-testid="mini-player-prev"
        aria-label="Previous"
      >
        <SkipBack className="w-3.5 h-3.5 text-gray-800" />
      </button>

      <button
        onClick={togglePlayPause}
        className="w-9 h-9 flex items-center justify-center rounded-full shadow-md"
        style={{ backgroundColor: color }}
        data-testid="mini-player-playpause"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
      </button>

      <button
        onClick={playNext}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/85 hover:bg-white shadow-sm"
        data-testid="mini-player-next"
        aria-label="Next"
      >
        <SkipForward className="w-3.5 h-3.5 text-gray-800" />
      </button>

      <button
        onClick={goHome}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/85 hover:bg-white shadow-sm"
        data-testid="mini-player-home"
        aria-label="Back to music page"
      >
        <Home className="w-3.5 h-3.5 text-gray-800" />
      </button>

      </div>
    </>
  );
};

export default MiniPlayer;
